import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Avatar } from '@/components/atoms/Avatar';
import { Badge } from '@/components/atoms/Badge';
import { Icon, Bell, User, Settings, LogOut } from '@/components/atoms/Icon';
import type { HeaderProps } from './Header.types';
import styles from './Header.module.scss';

export function Header({
  userName,
  userAvatar,
  userInitials = 'U',
  notificationCount = 0,
  onLogout,
  onProfileClick,
  onSettingsClick,
  onNotificationsClick,
  ...props
}: HeaderProps) {
  return (
    <header className={styles.header} {...props}>
      {/* Logo / Brand */}
      <div className={styles.header__brand}>
        <div className={styles.header__brand__title}>Knooowy</div>
        <span className={styles.header__brand__subtitle}>Stories</span>
      </div>

      {/* Right side: Notifications + User menu */}
      <div className={styles.header__actions}>
        {/* Notifications */}
        <button
          type="button"
          className={styles.header__notifications}
          onClick={onNotificationsClick}
          aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
        >
          <Icon icon={Bell} size="md" />
          {notificationCount > 0 && (
            <div className={styles.header__notifications__dot} />
          )}
        </button>

        {/* User Dropdown Menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className={styles.header__user} data-no-press-scale="" aria-label="User menu">
              <span className={styles.header__user__avatar}>
                <Avatar
                  src={userAvatar}
                  fallback={userInitials}
                  size="sm"
                  alt={userName || 'User'}
                />
                {notificationCount > 0 && (
                  <span className={styles.header__user__dot} aria-hidden />
                )}
              </span>
              {userName && <span className={styles.header__user__name}>{userName}</span>}
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.header__dropdown}
              align="end"
              sideOffset={8}
            >
              {/* Notifications — mobile only (bell button is hidden on mobile) */}
              {onNotificationsClick && (
                <DropdownMenu.Item
                  className={`${styles.header__dropdown__item} ${styles.header__dropdown__itemMobileOnly}`}
                  onSelect={onNotificationsClick}
                >
                  <Icon icon={Bell} size="sm" />
                  <span>Notifications</span>
                  {notificationCount > 0 && (
                    <Badge variant="secondary" size="sm" style={{ marginLeft: 'auto' }}>
                      {notificationCount}
                    </Badge>
                  )}
                </DropdownMenu.Item>
              )}
              {onNotificationsClick && (onProfileClick || onSettingsClick || onLogout) && (
                <DropdownMenu.Separator
                  className={`${styles.header__dropdown__separator} ${styles.header__dropdown__itemMobileOnly}`}
                />
              )}

              {/* Profile */}
              {onProfileClick && (
                <DropdownMenu.Item
                  className={styles.header__dropdown__item}
                  onSelect={onProfileClick}
                >
                  <Icon icon={User} size="sm" />
                  <span>Profile</span>
                </DropdownMenu.Item>
              )}

              {/* Settings */}
              {onSettingsClick && (
                <DropdownMenu.Item
                  className={styles.header__dropdown__item}
                  onSelect={onSettingsClick}
                >
                  <Icon icon={Settings} size="sm" />
                  <span>Settings</span>
                </DropdownMenu.Item>
              )}

              {/* Separator */}
              {(onProfileClick || onSettingsClick) && onLogout && (
                <DropdownMenu.Separator className={styles.header__dropdown__separator} />
              )}

              {/* Logout */}
              {onLogout && (
                <DropdownMenu.Item
                  className={styles.header__dropdown__item}
                  onSelect={onLogout}
                >
                  <Icon icon={LogOut} size="sm" />
                  <span>Logout</span>
                </DropdownMenu.Item>
              )}

              <DropdownMenu.Arrow className={styles.header__dropdown__arrow} />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}

export default Header;
